import React from 'react';
import axios from 'axios';
import { noop } from 'lodash';

import { useClaim } from '~/components/ClaimContainer';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import { reportAxiosError } from '~/Utils';
import type { PopulatedClaim } from '~/Utils/populateClaim';
import { populateClaim } from '~/Utils/populateClaim';

export function useFetchClaim(
  claimId?: number | null
): [PopulatedClaim | undefined, boolean, boolean, (() => Promise<void>) | (() => void)] {
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isError, setIsError] = React.useState<boolean>(false);
  const [fetchedClaim, setFetchedClaim] = React.useState<PopulatedClaim | undefined>(undefined);

  // NOTE: we could've optimized and set isLoading = !claim and fetchedClaim = claim, but than the case where claim context is changed will be almost never tested
  const { claim: claimInContext, onClaimUpdate = noop } = useClaim();

  // HACK: If claimId is undefined and we have claimInContext we use it for backward compatibility (For example ExposureMultiSelectTextFieldFormik).
  // TODO: fix this, if claimId is null we should return null and not claim in context, but this should be tested.
  const shouldUseClaimInContext = claimInContext && (claimInContext.id === claimId || !claimId);

  const fetchClaim = React.useCallback(async (): Promise<void> => {
    if (!claimId) return;

    try {
      const response = await axios.get<ClaimPolymorph>(`/api/v1/claims/${claimId}`);
      setFetchedClaim(populateClaim(response.data));
      setIsError(false);
    } catch (error) {
      setIsError(true);
      reportAxiosError(error);
    }
  }, [claimId]);

  React.useEffect(() => {
    if (!claimId) {
      setFetchedClaim(undefined);
      setIsLoading(false);
      return;
    }

    if (fetchedClaim && fetchedClaim.id === claimId) {
      return;
    }

    if (shouldUseClaimInContext) {
      setIsLoading(false);
      return;
    }

    fetchClaim().then(() => {
      setIsLoading(false);
    });
  }, [claimId, claimInContext, fetchedClaim, fetchClaim, shouldUseClaimInContext]);

  return [
    shouldUseClaimInContext ? claimInContext : fetchedClaim,
    isLoading,
    isError,
    shouldUseClaimInContext ? onClaimUpdate : fetchClaim,
  ];
}
